/**
 * Codec2 / Opus / PCM 下行开关与参数（.env）
 * 启用上行（全局默认）：REACT_APP_VOICE_UPLINK=codec2
 * PTT 实际编码：按 deviceId 记录下行来源——`…/downstream` → PCM 上行，`…/downstream/c2` → Codec2 上行；
 * 若两路同时有数据，记到过一次 c2 后不再被后续 pcm 包覆盖（避免误走 `audio/user/.../upstream`）。
 * 尚无下行样本时回退到本开关。
 *
 * 下行 MQTT：同时订阅 `audio/device/{id}/downstream`（PCM）与 `…/downstream/c2`（Codec2），
 * 由主题决定载荷类型。`REACT_APP_VOICE_DOWNSTREAM` 仅在未传入主题分支时作回退（如旧回调）。
 *
 * 上行：PCM → `audio/user/{id}/upstream`；Codec2 → `audio/user/{id}/upstream/c2`。
 */

function envTrim(key: string): string {
  return (process.env[key] || '').trim();
}

function envTruthy(value: string): boolean {
  const v = value.toLowerCase();
  return v === 'codec2' || v === '1' || v === 'true' || v === 'yes';
}

/** 下行语音格式（MQTT 载荷语义 / .env 回退） */
export type VoiceDownstreamMode = 'pcm' | 'codec2' | 'opus';

/** 与 MQTT 主题对应的下行音频种类（`handleAudioMessage` 传入播放层） */
export type MqttAudioDownstreamKind = 'pcm' | 'codec2' | 'opus';

export function getMqttAudioDownstreamKindFromTopic(topic: string): MqttAudioDownstreamKind {
  if (topic.includes('/downstream/c2')) {
    return 'codec2';
  }
  if (topic.includes('/downstream/opus')) {
    return 'opus';
  }
  return 'pcm';
}

/** PCM：audio/device/{id}/downstream */
export function getMqttAudioDownstreamPcmTopic(deviceId: string): string {
  return `audio/device/${deviceId}/downstream`;
}

/** Codec2：audio/device/{id}/downstream/c2 */
export function getMqttAudioDownstreamCodec2Topic(deviceId: string): string {
  return `audio/device/${deviceId}/downstream/c2`;
}

/** 传统 PCM 上行：audio/user/{id}/upstream */
export function getMqttAudioUpstreamUserTopic(deviceId: string): string {
  return `audio/user/${deviceId}/upstream`;
}

/** Codec2 上行：audio/user/{id}/upstream/c2 */
export function getMqttAudioUpstreamCodec2UserTopic(deviceId: string): string {
  return `audio/user/${deviceId}/upstream/c2`;
}

/** @deprecated 主题为 `audio/user/.../upstream/c2`，请用 getMqttAudioUpstreamCodec2UserTopic */
export function getMqttAudioUpstreamCodec2DeviceTopic(deviceId: string): string {
  return getMqttAudioUpstreamCodec2UserTopic(deviceId);
}

/**
 * @deprecated 请使用 getMqttAudioDownstreamPcmTopic；双主题订阅见 MqttAudioManager.subscribeToDeviceAudio
 */
export function getMqttAudioDownstreamTopic(deviceId: string): string {
  return getMqttAudioDownstreamPcmTopic(deviceId);
}

export function getVoiceDownstreamMode(): VoiceDownstreamMode {
  const raw = envTrim('REACT_APP_VOICE_DOWNSTREAM');
  const v = raw.toLowerCase();
  if (v === 'opus') {
    return 'opus';
  }
  if (envTruthy(raw)) {
    return 'codec2';
  }
  return 'pcm';
}

/** 是否走 Opus 下行（WebCodecs 解码 + 48k 主上下文 Worklet） */
export function isVoiceDownstreamOpus(): boolean {
  return getVoiceDownstreamMode() === 'opus';
}

/**
 * Opus MQTT 载荷分帧：0 = 整包即一帧 Opus；2 = 每帧前 uint16 BE 长度（可多包拼接）
 * .env: REACT_APP_OPUS_DOWNSTREAM_LENGTH_PREFIX，默认 0
 */
export function getOpusDownstreamLengthPrefixBytes(): 0 | 2 {
  const raw = envTrim('REACT_APP_OPUS_DOWNSTREAM_LENGTH_PREFIX');
  const n = parseInt(raw, 10);
  if (n === 2) {
    return 2;
  }
  return 0;
}

/** 是否走 Codec2 上行（8kHz 单声道 s16le → Worker → .bit → MQTT） */
export function isVoiceUplinkCodec2(): boolean {
  return envTruthy(envTrim('REACT_APP_VOICE_UPLINK'));
}

/** 是否走 Codec2 下行 */
export function isVoiceDownstreamCodec2(): boolean {
  return getVoiceDownstreamMode() === 'codec2';
}

/** 下行每帧字节数（须与后端一致；mode 3200 常见 8） */
export function getCodec2DownstreamFrameBytes(): number {
  const raw = envTrim('REACT_APP_CODEC2_DOWNSTREAM_FRAME_BYTES');
  const n = parseInt(raw, 10);
  if (Number.isFinite(n) && n > 0) {
    return n;
  }
  return 8;
}

/** c2enc/c2dec 模式字符串，须与后端/设备一致 */
export function getCodec2Mode(): string {
  const m = envTrim('REACT_APP_CODEC2_MODE');
  return m.length > 0 ? m : '3200';
}

/** 上行 8kHz mono s16le 每帧字节数（mode 3200：20ms → 160 样点 × 2 = 320） */
export function getCodec2UplinkMonoPcmBytesPerFrame(): number {
  const raw = envTrim('REACT_APP_CODEC2_UPLINK_PCM_BYTES_PER_FRAME');
  const n = parseInt(raw, 10);
  if (Number.isFinite(n) && n > 0) {
    return n;
  }
  return 320;
}

/**
 * Codec2 上行 MQTT 拼帧：编码比特尾衔接满 N 帧（帧长见 REACT_APP_CODEC2_DOWNSTREAM_FRAME_BYTES）再 publish。
 * 1 = 不拼包；默认 11 即 mode3200 常见 8B/帧 → 88 字节/条（与常见设备聚包一致）。
 * .env: REACT_APP_CODEC2_UPLINK_MQTT_BATCH_FRAMES
 */
export function getCodec2UplinkMqttBatchFrameCount(): number {
  const raw = envTrim('REACT_APP_CODEC2_UPLINK_MQTT_BATCH_FRAMES');
  if (raw === '') {
    return 11;
  }
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 1) {
    return 11;
  }
  return Math.min(n, 256);
}

/** 将上行 PCM 长度补整为 Codec2 一帧字节数的倍数（末尾补 0） */
export function padUplinkPcmToCodec2FrameMultiple(pcm: ArrayBuffer): ArrayBuffer {
  const frameBytes = getCodec2UplinkMonoPcmBytesPerFrame();
  const len = pcm.byteLength;
  const remainder = len % frameBytes;
  if (remainder === 0) {
    return pcm;
  }
  const padded = new ArrayBuffer(len + (frameBytes - remainder));
  new Uint8Array(padded).set(new Uint8Array(pcm));
  return padded;
}

/**
 * Codec2 下行专用：8kHz Worklet 环缓容量（交错 float，LRLR…）。
 * 不设时用 getAudioPlaybackRingBufferFloats() 按 8k/48k 比例折算。
 * .env: REACT_APP_CODEC2_8K_RING_BUFFER_FLOATS
 */
export function getCodec2DownstreamRingInterleavedFloats(sampleRateHz: number): number {
  const raw = envTrim('REACT_APP_CODEC2_8K_RING_BUFFER_FLOATS');
  const explicit = parseInt(raw, 10);
  if (Number.isFinite(explicit) && explicit >= 4096) {
    return Math.min(explicit, 524288);
  }
  const ringBase = getAudioPlaybackRingBufferFloats();
  return Math.max(4096, Math.round((ringBase * sampleRateHz) / 48000));
}

/**
 * Codec2 下行：8k Worklet 开声前最少交错样本数。
 * 不设时按 REACT_APP_AUDIO_MIN_BUFFER_SAMPLES 按采样率折算。
 * .env: REACT_APP_CODEC2_8K_MIN_BUFFER_SAMPLES
 */
export function getCodec2DownstreamMinInterleavedSamples8k(sampleRateHz: number): number {
  const raw = envTrim('REACT_APP_CODEC2_8K_MIN_BUFFER_SAMPLES');
  const explicit = parseInt(raw, 10);
  if (Number.isFinite(explicit) && explicit >= 0) {
    return Math.min(explicit, 192000);
  }
  const minBase = getAudioPlaybackMinInterleavedSamples();
  return Math.max(64, Math.round((minBase * sampleRateHz) / 48000));
}

/**
 * Codec2 下行：主线程上「已解码 PCM 段」FIFO 最大段数（每 MQTT 一包通常 1 段），超出则丢弃最旧段以控延迟。
 * 0 表示不限制段数。
 * .env: REACT_APP_CODEC2_DOWNSTREAM_PCM_QUEUE_MAX，默认 16
 */
export function getCodec2PlaybackMaxPcmQueueChunks(): number {
  const raw = envTrim('REACT_APP_CODEC2_DOWNSTREAM_PCM_QUEUE_MAX');
  const n = parseInt(raw, 10);
  if (Number.isFinite(n) && n >= 0) {
    return Math.min(n, 256);
  }
  return 16;
}

/**
 * AudioWorklet 环形缓冲区容量（交错 float 个数，双声道 LRLR…）
 * .env: REACT_APP_AUDIO_RING_BUFFER_FLOATS，默认 65536（约 682ms @ 48k 立体声）
 */
export function getAudioPlaybackRingBufferFloats(): number {
  const raw = envTrim('REACT_APP_AUDIO_RING_BUFFER_FLOATS');
  const n = parseInt(raw, 10);
  if (Number.isFinite(n) && n >= 4096) {
    return Math.min(n, 524288);
  }
  return 65536;
}

/**
 * 开始稳定播放前最少积压在环缓里的交错样本数（48k 双声道约每 ms 96 个 float）
 * .env: REACT_APP_AUDIO_MIN_BUFFER_SAMPLES，默认 4800（约 50ms）
 */
export function getAudioPlaybackMinInterleavedSamples(): number {
  const raw = envTrim('REACT_APP_AUDIO_MIN_BUFFER_SAMPLES');
  const n = parseInt(raw, 10);
  if (Number.isFinite(n) && n >= 0) {
    return Math.min(n, 192000);
  }
  return 4800;
}

/** .env: REACT_APP_AUDIO_AGGRESSIVE_RING_START=1 时任意非空缓冲即开声（更低延迟、更易欠载爆音） */
export function isAudioPlaybackAggressiveRingStart(): boolean {
  return envTruthy(envTrim('REACT_APP_AUDIO_AGGRESSIVE_RING_START'));
}

/**
 * Codec2 下行播放：预加重系数，0 关闭；建议 0.92–0.96，略提亮气音/齿音（仍受 8k 带宽限制）。
 * .env: REACT_APP_CODEC2_PLAYBACK_PREEMPHASIS
 */
export function getCodec2PlaybackPreemphasisCoefficient(): number {
  const raw = envTrim('REACT_APP_CODEC2_PLAYBACK_PREEMPHASIS');
  const x = parseFloat(raw);
  if (!Number.isFinite(x) || x <= 0) {
    return 0;
  }
  return Math.min(0.97, Math.max(0.85, x));
}

/**
 * Codec2 下行：置 1 时强制走「JS 8k→48k 立体声」再进主 AudioContext（旧路径）。
 * 默认关闭：使用独立 8kHz AudioContext + Worklet 环缓，不在前端做采样率上变换。
 */
export function isCodec2DownstreamForceResampleTo48k(): boolean {
  return envTruthy(envTrim('REACT_APP_CODEC2_PLAYBACK_RESAMPLE_TO_48K'));
}
